import { AlertColor } from '@mui/material';
import { MutateOptions, useMutation } from '@tanstack/react-query';
import get from 'lodash-es/get';
import { useRef, useState } from 'react';

import { batchAdd, batchProgress, batchUpdate } from '../api/ventura/location';
import { removeFileExtension } from '../util/helpers';
import { snakeCaseKeys } from '../util/object';
import { useConfig } from './useConfig';

export interface BatchLocationAlert {
  severity: AlertColor;
  message: string;
}

const PROGRESS_POLL_MS = 5000;

export const SAVING_MESSAGE =
  'File validated. Saving locations to the ad group; this can take several minutes and they appear all at once ' +
  'when done. You will also get an email with the result.';

// Starts a Ventura location batch_create/batch_update for a file already in S3 and
// polls its progress until Ventura reports "Completed…". `alert` carries the
// latest status; callers also use setAlert for their own upload/validation steps.
function useBatchLocationJob(adGroupId: number, isUpdate = false) {
  const { apiHost } = useConfig();
  const [alert, setAlert] = useState<BatchLocationAlert | null>(null);
  const keyRef = useRef('');

  const progress = useMutation({
    mutationFn: () => batchProgress(apiHost, removeFileExtension(keyRef.current)),
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    onSuccess: (response) => trackProgress(response),
  });

  function trackProgress(response) {
    const status = get(response, 'status', '');
    const completed = status.startsWith('Completed');
    // Ventura's percentage covers only its validation pass; the write that follows
    // reports nothing until it commits every row at once and says "Completed…".
    const message = status === 'Progress: 100%' ? SAVING_MESSAGE : status;
    setAlert({ severity: completed ? 'success' : 'info', message });
    if (!completed) {
      setTimeout(() => progress.mutate(), PROGRESS_POLL_MS);
    }
  }

  const job = useMutation({
    mutationFn: (key: string) => {
      const payload = snakeCaseKeys({ adGroupId, key });
      return isUpdate ? batchUpdate(apiHost, adGroupId, payload) : batchAdd(apiHost, adGroupId, payload);
    },
    onSuccess: trackProgress,
  });

  const start = (key: string, options?: MutateOptions<any, unknown, string>) => {
    keyRef.current = key;
    job.mutate(key, options);
  };

  return { start, alert, setAlert, isStarting: job.isLoading };
}

export default useBatchLocationJob;
